import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useCurrentEditor } from '@tiptap/react';

import { defaultExtensions, headingsOnlyExtensions } from '@/components/editor/extensions';
import {
  INLINE_RICH_TEXT_CONTENT_CLASS,
  INLINE_RICH_TEXT_EDIT_SURFACE_CLASS,
  INLINE_RICH_TEXT_SURFACE_CLASS,
} from '@/components/editor/inline-rich-text-surface';
import { FloatingRichTextToolbar } from '@/components/editor/toolbar/floating-rich-text-toolbar';
import { TableContextMenu } from '@/components/editor/toolbar/table-context-menu';
import { cn } from '@/lib/utils';
import { BaseEditableContent } from './base-editable-content';
import type { EditorFocusPoint } from './focus-editor-at-point';
import { RichTextPreview } from './rich-text-preview';

export type RichTextPersistMode = 'change' | 'flush-only';

export interface ControlledRichTextEditorProps {
  value: string;
  isEditing: boolean;
  onChange: (html: string) => void;
  persistMode?: RichTextPersistMode;
  onFocusChange?: (focused: boolean) => void;
  onExitEditing?: () => void;
  toolbarAnchorRef?: RefObject<HTMLElement | null>;
  previewClassName?: string;
  placeholder?: string;
  headingsOnly?: boolean;
  maxPlainTextLength?: number;
}

function TipTapReadySignal({ onReady }: { onReady: () => void }) {
  const { editor } = useCurrentEditor();

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    onReady();
  }, [editor, onReady]);

  return null;
}

/**
 * Rich text editor with explicit edit/preview control and optional floating toolbar.
 * Used by block grid, collection cards, footer, and pinned section fields.
 *
 * TipTap mounts only while `isEditing` is true; idle fields render `RichTextPreview`.
 * While TipTap boots (`immediatelyRender: false` → EditorProvider returns null), the
 * preview stays in normal flow while the editor mounts invisibly out of flow. Once ready,
 * the editor replaces it without introducing a formatting context that would block CSS floats.
 *
 * Click caret placement is captured on the preview (capture phase) and passed into
 * TipTap on mount — TipTap is not present while idle, so hit-testing cannot live there.
 */
export function ControlledRichTextEditor({
  value,
  isEditing,
  onChange,
  persistMode = 'change',
  onFocusChange,
  onExitEditing,
  toolbarAnchorRef,
  previewClassName,
  placeholder,
  headingsOnly = false,
  maxPlainTextLength,
}: ControlledRichTextEditorProps) {
  const [editorReady, setEditorReady] = useState(false);
  const focusPointRef = useRef<EditorFocusPoint | null>(null);
  const onlyPersistOnFlush = persistMode === 'flush-only';

  useEffect(() => {
    if (!isEditing) {
      setEditorReady(false);
      focusPointRef.current = null;
    }
  }, [isEditing]);

  const handleEditorReady = useCallback(() => {
    setEditorReady(true);
  }, []);

  const contentClassName = cn(
    INLINE_RICH_TEXT_CONTENT_CLASS,
    INLINE_RICH_TEXT_SURFACE_CLASS,
    previewClassName,
  );

  const preview = (
    <div
      onMouseDownCapture={(event) => {
        if (isEditing || event.button !== 0) return;
        focusPointRef.current = {
          clientX: event.clientX,
          clientY: event.clientY,
        };
      }}
    >
      <RichTextPreview
        value={value}
        className={contentClassName}
        placeholder={placeholder}
        headingsOnly={headingsOnly}
      />
    </div>
  );

  if (!isEditing) {
    return preview;
  }

  return (
    <div className="relative w-full">
      {!editorReady && <div key="preview">{preview}</div>}
      <div
        key="editor"
        aria-hidden={!editorReady}
        className={cn(!editorReady && 'pointer-events-none invisible absolute inset-x-0 top-0')}
      >
        <BaseEditableContent
          value={value}
          placeholder={placeholder}
          baseExtensions={headingsOnly ? headingsOnlyExtensions : defaultExtensions}
          onChangeHtml={onlyPersistOnFlush ? () => {} : onChange}
          onFlushHtml={onChange}
          onlyPersistOnFlush={onlyPersistOnFlush}
          editable
          autoFocus={editorReady}
          initialFocusPoint={focusPointRef.current}
          onFocusChange={onFocusChange}
          onExitEditing={onExitEditing}
          editorClassName={cn(contentClassName, INLINE_RICH_TEXT_EDIT_SURFACE_CLASS)}
          maxPlainTextLength={maxPlainTextLength}
        >
          <TipTapReadySignal onReady={handleEditorReady} />
          {toolbarAnchorRef ? (
            <FloatingRichTextToolbar
              anchorRef={toolbarAnchorRef}
              headingsOnly={headingsOnly}
              visible={editorReady}
            />
          ) : null}
          {!headingsOnly && editorReady ? <TableContextMenu /> : null}
        </BaseEditableContent>
      </div>
    </div>
  );
}
